import React, { CSSProperties } from "react";
import { Skeleton } from "@heroui/skeleton";
import "../../../../styles/heroui-skeleton.css";
import "./dashboardSkeletons.css";

type SkeletonShape = "rounded" | "pill" | "circle" | "ring";

const shapeRadius: Record<SkeletonShape, CSSProperties["borderRadius"]> = {
  rounded: 6,
  pill: 999,
  circle: "50%",
  ring: "50%",
};

export const SkeletonBlock = ({
  width,
  height,
  shape = "rounded",
  className = "",
  style,
}: {
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  shape?: SkeletonShape;
  className?: string;
  style?: CSSProperties;
}) => (
  <Skeleton
    aria-hidden="true"
    className={`nd-skeleton flex-shrink-0${shape === "ring" ? " nd-skeleton-ring" : ""}${
      className ? ` ${className}` : ""
    }`}
    style={{ width, height, borderRadius: shapeRadius[shape], ...style }}
  />
);

const REVENUE_BAR_HEIGHTS = [44, 58, 36, 66, 50, 78, 46, 62, 54, 72, 40, 64];

export const RevenueChartSkeleton = () => (
  <div className="d-flex gap-3 pt-3 pb-4" style={{ height: 340 }}>
    <div className="d-flex flex-column justify-content-between">
      {Array.from({ length: 7 }, (_, index) => (
        <SkeletonBlock key={index} width={34} height={10} />
      ))}
    </div>
    <div className="flex-fill d-flex align-items-end gap-2 min-w-0">
      {REVENUE_BAR_HEIGHTS.map((height, index) => (
        <SkeletonBlock key={index} height={`${height}%`} style={{ flex: "1 1 0", borderRadius: 4 }} />
      ))}
    </div>
  </div>
);

export const TrafficDonutSkeleton = () => (
  <div className="d-flex align-items-center justify-content-center" style={{ height: 270 }}>
    <SkeletonBlock width={196} height={196} shape="ring" />
  </div>
);

const TRAFFIC_ROW_WIDTHS = [58, 44, 52, 38, 30];

export const TrafficListSkeleton = () => (
  <div className="mb-1">
    {TRAFFIC_ROW_WIDTHS.map((width, index) => (
      <div
        key={index}
        className={`px-3 d-flex align-items-center justify-content-between gap-3 ${
          index === TRAFFIC_ROW_WIDTHS.length - 1 ? "pt-2 pb-3" : "py-2 border-bottom"
        }`}
      >
        <div className="d-flex align-items-center gap-2 flex-fill min-w-0">
          <SkeletonBlock width={8} height={8} shape="circle" />
          <SkeletonBlock width={`${width}%`} height={12} />
        </div>
        <SkeletonBlock width={32} height={12} />
      </div>
    ))}
  </div>
);

export const KpiCardSkeleton = () => (
  <div className="card flex-fill">
    <div className="card-body d-flex align-items-start justify-content-between gap-3">
      <div className="flex-fill min-w-0">
        <SkeletonBlock width="45%" height={12} className="mb-2" />
        <SkeletonBlock width="60%" height={24} className="mb-3" />
        <div className="d-flex align-items-center gap-2">
          <SkeletonBlock width={44} height={18} shape="pill" />
          <SkeletonBlock width="40%" height={12} />
        </div>
      </div>
      <SkeletonBlock width={48} height={48} shape="circle" />
    </div>
  </div>
);

const TOP_DEAL_ROW_WIDTHS = [72, 56, 64, 48, 60];

export const TopDealsSkeleton = () => (
  <div className="mb-2">
    {TOP_DEAL_ROW_WIDTHS.map((width, index) => (
      <div key={index} className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-center gap-2 flex-fill min-w-0">
          <SkeletonBlock width={45} height={45} shape="circle" />
          <div className="flex-fill min-w-0">
            <SkeletonBlock width={`${width}%`} height={13} className="mb-2" />
            <SkeletonBlock width={`${width - 22}%`} height={11} />
          </div>
        </div>
        <SkeletonBlock width={56} height={14} />
      </div>
    ))}
  </div>
);

const PIPELINE_BAR_HEIGHTS = ["100%", "72%", "48%"];

export const PipelineSkeleton = () => (
  <>
    <div className="row g-3 mb-3">
      {PIPELINE_BAR_HEIGHTS.map((_, index) => (
        <div className="col-4" key={index}>
          <SkeletonBlock width="70%" height={12} className="mb-2" />
          <SkeletonBlock width="55%" height={16} className="mb-2" />
          <SkeletonBlock width="45%" height={12} />
        </div>
      ))}
    </div>
    <div className="d-flex align-items-end gap-1" style={{ height: 64 }}>
      {PIPELINE_BAR_HEIGHTS.map((height, index) => (
        <SkeletonBlock key={index} height={height} style={{ flex: "1 1 0", borderRadius: 2 }} />
      ))}
    </div>
  </>
);

const DEAL_SEGMENT_WIDTHS = [46, 38, 42, 40];

export const DealsOverviewSkeleton = () => (
  <>
    <SkeletonBlock width="100%" height={8} shape="pill" className="mb-3" />
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <SkeletonBlock width={56} height={24} />
        <SkeletonBlock width={44} height={18} shape="pill" />
        <SkeletonBlock width={130} height={12} />
      </div>
      {DEAL_SEGMENT_WIDTHS.map((width, index) => (
        <div
          key={index}
          className={`p-2 d-flex align-items-center justify-content-between gap-3${
            index === DEAL_SEGMENT_WIDTHS.length - 1 ? "" : " border-bottom"
          }`}
        >
          <div className="d-flex align-items-center gap-2 flex-fill min-w-0">
            <SkeletonBlock width={8} height={8} shape="circle" />
            <SkeletonBlock width={`${width}%`} height={12} />
          </div>
          <SkeletonBlock width={60} height={12} />
        </div>
      ))}
    </div>
    <div className="p-3 border rounded d-flex align-items-center justify-content-between nd-skeleton-panel">
      <div>
        <SkeletonBlock width={72} height={12} className="mb-2" />
        <SkeletonBlock width={40} height={22} />
      </div>
      <SkeletonBlock width={48} height={48} shape="circle" />
    </div>
  </>
);

const RECENT_DEAL_NAME_WIDTHS = [140, 112, 158, 124, 134];

export const RecentDealsSkeletonRows = () => (
  <>
    {RECENT_DEAL_NAME_WIDTHS.map((width, index) => (
      <tr key={index}>
        <td>
          <SkeletonBlock width={width} height={13} />
        </td>
        <td>
          <SkeletonBlock width={72} height={12} />
        </td>
        <td>
          <SkeletonBlock width={60} height={12} className="ms-auto" />
        </td>
        <td>
          <SkeletonBlock width={46} height={18} shape="pill" />
        </td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <SkeletonBlock width={24} height={24} shape="circle" />
            <SkeletonBlock width={84} height={12} />
          </div>
        </td>
        <td>
          <SkeletonBlock width={52} height={18} shape="pill" />
        </td>
      </tr>
    ))}
  </>
);
